import React, { useEffect, useState } from 'react';
import './App.css';
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { useThemeMode } from "./theme/colorThemeContext";
import ProtectedRoute from './pages/login/protectedRoute';
import ProtectedMenuRoute from './components/ProtectedMenuRoute'; 
import Clusters from './pages/clusters';
import Organizations from './pages/adminSettings/organizations';
import AddNewClient from './pages/adminSettings/addNewClient';
import Project from './pages/project';
import ApplicationTemplate from './pages/applicationTemplate';
import AddTemplate from './pages/addTemplate';
import AddNewProject from './pages/addNewProject';
import Dashboard from './pages/Dashboard';
import TicketRequests from './pages/checkStatus';
import ApplicationFormPage from './pages/applicationForm';
import Login from './pages/login';
import Services from './pages/services';
import ManagedDiskSelect from './pages/diskPrice';
import CreateResource from './pages/requestResource';
import Resource from './pages/resource';
import CreateTemplatePage from './pages/test';
import AddOperatingSys from './pages/addOperatingSystem';
import { SelectedProviderProvider } from './services/selectedProiderContext';
import CreateUser from './pages/adminSettings/CreateUser';
import ServicesNew from './pages/servicesNew';
import ServicesExist from './pages/serviceExist';
import Discount from './pages/Discount';
import { AuthProvider } from './services/authContext';
import AddDiscount from './pages/addDiscount';
import ApplicationNameDetails from './pages/ApplicationNameDetails';
import ListUser from './pages/adminSettings/userList';
import ApproverSettings from './pages/approvalSettings';
import AssignApproval from './pages/assignApproval';
import RolesManagement from './pages/rolesManagement';
import CloudSetup from './pages/adminSettings/cloudInformation';
import Report from './pages/report';
import RoleDashboard from './components/Dashboard/roleDashboard';
import AuditPage from './pages/auditPage';
import AccessDenied from './pages/accessDenied';
import Unauthorized from './pages/Unauthorized';
import { AdminFieldConfig } from './pages/AdminFieldConfig';
import AuthRedirect from './services/AuthRedirect';
import msalConfig from './services/msalConfg';
import AddSetup from './pages/adminSettings/AddSetup';
import Setup from './pages/adminSettings/Setup';
import CloudDiagram from './components/workflow/CloudDiagram';
import AdminSetup from './pages/adminSettings/AdminSetup';
import SqlExecutor from './pages/sqlExecuter/sqlExecutor';
import AddAdminSetup from './pages/adminSettings/AddAdminSetup';
import RedrictURL from "./pages/login/redirecturl";
import Faq from './pages/faq';
import RedrictURLValidUser from './pages/login/RedrictURLValidUser';
import { checkTokenExpiration } from "./Utils/common";
import SuggestionRoutes from './pages/suggestion/SuggestionRoutes';
import TemplateGallery from './pages/templateGallery/TemplateGallery';
import { ConfigProvider, useConfig } from "./services/ConfigContext";
import AdminLambda from './pages/lamdasetup/adminlamda';
import Bulktags from './pages/bulkTags/Bulktags';
import AwsMasterData from './pages/bulkTags/awsMasterData';
import { hasPermission } from './Utils/role';



const msalInstance = new PublicClientApplication(msalConfig);

// Blocks access if the current user has IS_SUPER_ADMIN permission
const BlockAdminRoute = ({ children }) => {
  if (hasPermission("IS_SUPER_ADMIN")) {
    return <Navigate to="/access-denied" replace />;
  }
  return children;
};

function AppContent() {
  const { toggleColorMode } = useThemeMode();
  const location = useLocation();
  const history = useNavigate();
  const [tokenExpireTime, setTokenExpireTime] = useState("");
  const [token, setToken] = useState("");
  const { config } = useConfig();

  useEffect(() => {
    if (location.pathname === '/login') return;

    const storedToken = sessionStorage.getItem('accessToken');
    const storedTokenExpireTime = sessionStorage.getItem('tokenExpireTime');
    const storedSessionStartTime = sessionStorage.getItem('sessionStartTime');

    if (storedToken && storedTokenExpireTime && storedSessionStartTime) {
      setToken(storedToken);
      setTokenExpireTime(Number.parseInt(storedTokenExpireTime, 10));
    }

    globalThis.tokenInterval = setInterval(() => {
      checkTokenExpiration(
        sessionStorage.getItem('accessToken'), 
        Number.parseInt(sessionStorage.getItem('tokenExpireTime') || '0', 10), 
        Number.parseInt(sessionStorage.getItem('sessionStartTime') || '0', 10), 
        history, 
        { setToken }
      );
    }, 10000); 

    return () => clearInterval(globalThis.tokenInterval);
  }, [token, tokenExpireTime, location.pathname, history]);

  useEffect(() => {
    const excludedRoutes = ['/', '/login', '/auth-redirect', '/access-denied'];
    if (!excludedRoutes.includes(location.pathname)) {
      localStorage.setItem('lastVisitedRoute', location.pathname);
    }
  }, [location]);

  return (
    <SelectedProviderProvider>
      <AuthProvider>
        <Routes location={location} key={location.key}>
          {/* Public & Auth Routes */}
          <Route path="/" element={<Login />} />
          <Route path="/auth-redirect" element={<AuthRedirect />} />
          <Route path="/auth/callback" element={<RedrictURL setToken={setToken} />} />
          <Route path="/validuser" element={<RedrictURLValidUser />} />
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="/SqlExecutor" element={<SqlExecutor />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Account Selection / Top-Level Protected Routes */}
          <Route path='/provider' element={<ProtectedRoute><Project toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/dashboardRole' element={<ProtectedRoute><RoleDashboard toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path="/clusters" element={<ProtectedRoute><Clusters /></ProtectedRoute>} />
          
          {/* Admin Configuration Routes (Guarded by ProtectedRoute to ensure account selection) */}
          <Route path='/organizations' element={<ProtectedRoute><Organizations toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/Add_Organization' element={<ProtectedRoute><AddNewClient toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/application_template' element={<ProtectedRoute><ApplicationTemplate toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/application_template/add_template' element={<ProtectedRoute><AddTemplate toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/add_new_tenant' element={<ProtectedRoute><AddNewProject toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/application_template/application_form' element={<ProtectedRoute><ApplicationFormPage toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/application_template/operating_system' element={<ProtectedRoute><AddOperatingSys toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/createuser' element={<ProtectedRoute><CreateUser toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/user_list' element={<ProtectedRoute><ListUser toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/manage_Approver' element={<ProtectedRoute><ApproverSettings toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/delegate_approver' element={<ProtectedRoute><AssignApproval toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/roles_management' element={<ProtectedRoute><RolesManagement toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/cloud_settings' element={<ProtectedRoute><CloudSetup toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/lambda' element={<ProtectedRoute><AdminLambda toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/fieldcongfig' element={<ProtectedRoute><AdminFieldConfig toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/bulkEC2Provisioning' element={<ProtectedRoute><Bulktags toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/settings' element={<ProtectedRoute><Setup toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/AddSetup' element={<ProtectedRoute><AddSetup toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/AdminSetup' element={<ProtectedRoute><AdminSetup toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          <Route path='/AddAdminSetup' element={<ProtectedRoute><AddAdminSetup toggleTheme={toggleColorMode} /></ProtectedRoute>} />

          <Route element={<ProtectedMenuRoute />}>
            {/* Dashboard: Accessible by both clients and admins to select/view their initial account */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

            {/* Routes explicitly blocked for Super Admin */}
            <Route 
              path='/Resources' 
              element={
                <ProtectedRoute>
                    <Services toggleTheme={toggleColorMode} />
                </ProtectedRoute>
              } 
            />

            {/* General App Pages */}
            <Route path='/audit' element={<ProtectedRoute><AuditPage toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/provision-status' element={<ProtectedRoute><TicketRequests toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/faq' element={<ProtectedRoute><Faq toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/network-view' element={<ProtectedRoute><CloudDiagram toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/awsMasterData' element={<ProtectedRoute><AwsMasterData toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            
            <Route 
              path="/suggestions/*" 
              element={
                config?.isRoute ? (
                  <ProtectedRoute>
                    <SuggestionRoutes toggleTheme={toggleColorMode}/>
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/Resources" replace />
                )
              } 
            />

            <Route path='/Resources/View_Resources' element={<ProtectedRoute><Resource toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/Resources/View_Resources/template-gallery' element={<TemplateGallery />} />
            <Route path='/Resources/Create_Request' element={<ProtectedRoute><CreateResource toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/Resources/Operational_Resources' element={<ProtectedRoute><ServicesExist toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            
            

            <Route path='/diskselect' element={<ProtectedRoute><ManagedDiskSelect toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/test' element={<ProtectedRoute><CreateTemplatePage toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/Discount' element={<ProtectedRoute><Discount toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/AddDiscount' element={<ProtectedRoute><AddDiscount toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/ApplicationName' element={<ProtectedRoute><ApplicationNameDetails toggleTheme={toggleColorMode} /></ProtectedRoute>} />
            <Route path='/report/:resourceReqId' element={<ProtectedRoute><Report toggleTheme={toggleColorMode} /></ProtectedRoute>} />
          </Route>

          {/* Catch-all fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </SelectedProviderProvider>
  );
}

function App() {
  return (
    <ConfigProvider>
      <MsalProvider instance={msalInstance}>
        <AppContent />
      </MsalProvider>
    </ConfigProvider>
  );
}

export default App;